import { AfterViewInit, Directive, ElementRef, Inject, Input, OnDestroy } from '@angular/core';
import { TransitionService, Transition, UrlService, UIView, ParentUIViewInject } from '@uirouter/angular';

import { isMobileSafariWithDialogBlocking } from 'rev-shared/util/UserAgentUtil';

/**
 * Utility component to show a confirmation dialog if the user attempts to close the browser,
 * navigate to anther page in rev, or another site.
 */
@Directive({
	selector: '[vbNavigationHandler]'
})
export class VbNavigationHandlerDirective implements AfterViewInit, OnDestroy {
	@Input() public locationChangeMessage: string; // Confirmation message. Shown before a state change, or url change.
	@Input() public unloadMessage: string; // Confirmation message. Shown before the page unloads.

	// Return false to show a confirmation message and block navigation.
	@Input() public onNavigate: () => boolean;

	private readonly transitionDebounceTime = 1000;
	private contextStateName: string;
	private isConfirmAllowed: boolean;
	private lastConfirmationTime: number = 0;
	private removeTransitionOnStartHandler: () => void;
	private unsubOnUrlChange: () => void;

	constructor(
		private el: ElementRef,
		private $transitions: TransitionService,
		private url: UrlService,
		@Inject(UIView.PARENT_INJECT) private parent: ParentUIViewInject
	) {}

	public ngAfterViewInit(): void {
		this.contextStateName = this.parent.context.name;
		this.isConfirmAllowed = !isMobileSafariWithDialogBlocking();

		this.unsubOnUrlChange = this.url.onChange(e => this.onLocationChangeStart(e)) as () => void;
		this.removeTransitionOnStartHandler = this.$transitions.onStart({}, this.onTransitionStart) as () => any;
		window.addEventListener('beforeunload', this.onBeforeUnload);
	}

	public ngOnDestroy(): void {
		window.removeEventListener('beforeunload', this.onBeforeUnload);
		this.removeTransitionOnStartHandler();
		this.unsubOnUrlChange?.();
	}

	private confirmLocationChange(): boolean {
		if(this.isConfirmAllowed) {
			const confirmed = Date.now() - this.lastConfirmationTime < this.transitionDebounceTime
				|| window.confirm(this.locationChangeMessage);
			if(confirmed) {
				this.lastConfirmationTime = Date.now();
			}
			return confirmed;
		}
	}

	private isChildState(stateName: string): boolean {
		return stateName.startsWith(this.contextStateName);
	}

	private onBeforeUnload = (): string => {
		if (this.onNavigate() === false) {
			return this.unloadMessage;
		}
	};

	private onLocationChangeStart = (event: any): void => {
		if(this.onNavigate() === false && !this.confirmLocationChange()) {
			event.preventDefault();
		}
	};

	private onTransitionStart = (transition: Transition): boolean => {
		if (this.isChildState(transition.to().name)) {
			return;
		}

		if (this.onNavigate() === false && !this.confirmLocationChange()) {
			return false;
		}
	};
}
